import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";

export interface PaginationControlsProps {
  currentPage: number;
  totalPages: number;
  total: number;
  pageSize: number;
  startIndex: number;
  endIndex: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  itemLabel?: string;
  pageSizeOptions?: number[];
  testIdPrefix?: string;
  className?: string;
}

export function PaginationControls({
  currentPage,
  totalPages,
  total,
  pageSize,
  startIndex,
  endIndex,
  onPageChange,
  onPageSizeChange,
  itemLabel = "items",
  pageSizeOptions = [10, 25, 50, 100],
  testIdPrefix = "",
  className = "",
}: PaginationControlsProps) {
  const prefix = testIdPrefix ? `${testIdPrefix}-` : "";

  return (
    <div 
      className={`flex items-center justify-between gap-2 sm:gap-4 px-4 py-3 ${className}`}
      data-testid={`${prefix}pagination-controls`}
    >
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span data-testid={`${prefix}pagination-showing`} className="whitespace-nowrap">
          <span className="hidden sm:inline">Showing </span>{startIndex}–{endIndex}<span className="hidden sm:inline"> of {total} {itemLabel}</span>
        </span>
        <Select 
          value={String(pageSize)} 
          onValueChange={(val) => onPageSizeChange(parseInt(val, 10))}
        >
          <SelectTrigger className="w-[60px] sm:w-[80px] h-8" data-testid={`${prefix}pagination-pagesize`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {pageSizeOptions.map((size) => (
              <SelectItem key={size} value={String(size)}>
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="hidden sm:inline">per page</span>
      </div>
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          className="hidden sm:inline-flex"
          data-testid={`${prefix}pagination-first`}
        >
          <ChevronsLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          data-testid={`${prefix}pagination-prev`}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="px-2 sm:px-3 py-1 text-sm whitespace-nowrap">
          {currentPage}<span className="hidden sm:inline"> of</span><span className="sm:hidden">/</span>{totalPages}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          data-testid={`${prefix}pagination-next`}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage >= totalPages}
          className="hidden sm:inline-flex"
          data-testid={`${prefix}pagination-last`}
        >
          <ChevronsRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
